import type { ReactNode } from 'react';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import Link from '@swf/components/Link/Link';
import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import { Country, EulaType, ResolverTag, Site } from '@swf/core/enums';
import { select } from '@swf/core/store';

import type { Props as RegulatoryCheckboxesProps } from '../RegulatoryCheckboxes';

import {
  TXTCLI_By_checking_this_box_I_confirm_ELL,
  TXTCLI_I_confirm_that_I_am_at_least_N_ELL,
  TXTCLI_I_confirm_that_I_am_at_least_N_ELL_1,
  TXTCLI_I_consent_to_receive_details_a_ELL,
  TXTCLI_I_have_read_and_understood_the_ELL_6,
  TXTCLI_I_have_read_and_understood_the_ELL_7,
  TXTCLI_I_have_read_and_understood_the_ELL_8,
  TXTCLI_I_understand_that_I_can_always_ELL,
  TXTCLI_The_information_I_have_provide_ELL,
  TXTMOB_I_consent_to_receive_details_o_ELL,
  TXTWCL_I_am_Xa_hrefXXN0XXeligibleX_aX_ELL_1,
  TXTWCL_I_confirm_that_I_am_acting_on__ELL,
  TXTWCL_Underage_gambling_is_an_offenc_ELL,
  TXTWCL_We_do_not_offer_real_money_pla_ELL
} from './useLabel.messages.json';

enum AREulaState {
  BUENOS_AIRES = 'B',
  CABA = 'C'
}

type Props = RegulatoryCheckboxesProps & { eulaType: EulaType };

const isAREulaState = (country?: string, state?: string) =>
  country === Country.Argentina && Object.values(AREulaState).includes(state as AREulaState);

const trackableLink = (msg: ReactNode, onClick?: () => void) => (
  <Link
    onClick={(e: React.MouseEvent) => {
      e.preventDefault();
      onClick?.();
    }}
  >
    {msg}
  </Link>
);

const resolverLink = (msg: string, resolverTag: ResolverTag) => (
  <ResolverLink resolverTag={resolverTag}>{msg}</ResolverLink>
);

const useLabel = (
  onEulaLinkClick: Props['onEulaLinkClick'],
  onPrivacyLinkClick: Props['onPrivacyLinkClick']
) => {
  const siteId = useSelector(select.site.getId);
  const legalAge = useSelector(select.site.getLegalAge);
  const history = useHistory<{ country: string; state: string }>();

  const getEulaResolverTag = useCallback(() => {
    const { country, state } = history?.location?.state || {};
    return isAREulaState(country, state) ? ResolverTag.MCEulaARStates : ResolverTag.MCEula;
  }, [history?.location?.state]);

  return useCallback(
    (variant: Props['variant'], eulaType: Props['eulaType']) => {
      const eulaResolverTag = getEulaResolverTag();

      const labels = {
        first: {
          [EulaType.Eula]: [
            {
              ...TXTCLI_I_confirm_that_I_am_at_least_N_ELL,
              values: {
                0: legalAge,
                a: (msg: ReactNode) => trackableLink(msg, onEulaLinkClick)
              }
            }
          ],
          [EulaType.Privacy]: [
            {
              ...TXTCLI_I_have_read_and_understood_the_ELL_6,
              values: {
                a: (msg: ReactNode) => trackableLink(msg, onPrivacyLinkClick)
              }
            }
          ],
          [EulaType.CombinedEulaPrivacy]: [
            {
              ...TXTCLI_I_confirm_that_I_am_at_least_N_ELL_1,
              values: {
                0: legalAge,
                a: (msg: ReactNode) => trackableLink(msg, onEulaLinkClick),
                a2: (msg: ReactNode) => trackableLink(msg, onPrivacyLinkClick)
              }
            }
          ],
          [EulaType.IAmEligibleToPlay]: [
            {
              tagName: 'span',
              ...TXTWCL_I_am_Xa_hrefXXN0XXeligibleX_aX_ELL_1,
              values: {
                a: (msg: ReactNode) => trackableLink(msg, onEulaLinkClick)
              }
            }
          ]
        },
        rmf: {
          [EulaType.PlayerIsNot3Party]: [{ ...TXTWCL_I_confirm_that_I_am_acting_on__ELL }],
          [EulaType.InformationIsComplete]: [{ ...TXTCLI_The_information_I_have_provide_ELL }]
        },
        stars: {
          [EulaType.Eula]:
            siteId === Site.UK
              ? [
                  { tagName: 'b', ...TXTWCL_Underage_gambling_is_an_offenc_ELL },
                  {
                    ...TXTCLI_I_confirm_that_I_am_at_least_N_ELL,
                    values: {
                      0: legalAge,
                      a: (msg: string) => resolverLink(msg, eulaResolverTag)
                    }
                  }
                ]
              : [
                  {
                    ...TXTCLI_I_confirm_that_I_am_at_least_N_ELL,
                    values: {
                      0: legalAge,
                      a: (msg: string) => resolverLink(msg, eulaResolverTag)
                    }
                  }
                ],
          [EulaType.Privacy]: [
            {
              ...TXTCLI_I_have_read_and_understood_the_ELL_6,
              values: {
                a: (msg: string) => resolverLink(msg, ResolverTag.MCPrivacy)
              }
            }
          ],
          [EulaType.CombinedEulaPrivacy]: [
            {
              tagName: 'p',
              ...TXTCLI_I_confirm_that_I_am_at_least_N_ELL_1,
              values: {
                0: legalAge,
                a: (msg: string) => resolverLink(msg, eulaResolverTag),
                a2: (msg: string) => resolverLink(msg, ResolverTag.MCPrivacy)
              }
            }
          ],
          [EulaType.MarketingMailList]:
            siteId === Site.IT
              ? [
                  { ...TXTCLI_I_consent_to_receive_details_a_ELL },
                  { ...TXTCLI_I_understand_that_I_can_always_ELL }
                ]
              : [{ ...TXTMOB_I_consent_to_receive_details_o_ELL }]
        },
        default: {
          [EulaType.GamingAccAgreement]: [
            {
              tagName: 'p',
              ...TXTCLI_I_have_read_and_understood_the_ELL_7,
              values: {
                a: (msg: string) => resolverLink(msg, ResolverTag.GamingAccountAgreement)
              }
            }
          ],
          [EulaType.GameRulesConditions]: [
            { tagName: 'p', ...TXTCLI_I_have_read_and_understood_the_ELL_8 }
          ],
          [EulaType.IndianStatesRestrictions]: [
            { ...TXTWCL_We_do_not_offer_real_money_pla_ELL },
            { ...TXTCLI_By_checking_this_box_I_confirm_ELL }
          ]
        }
      };

      return labels[variant][eulaType] || labels.default[eulaType];
    },
    [legalAge, siteId, getEulaResolverTag, onEulaLinkClick, onPrivacyLinkClick]
  );
};

export default useLabel;
